import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton, Input, TextField } from "@mui/material";

import MainContainer from "../../layout/MainContainer";
import {
  useLazyVisaTypeDetailsQuery,
  useUpdateVisaTypeMutation,
} from "../../service/visaType";
import { errorToast, successToast } from "../../helpers";
import { useFormik } from "formik";
import { UploadMedia } from "../../utils/mediaUpload";
import * as Yup from "yup";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import Loader from "../../constants/Loader";
import { isValidInput } from "../../utils/validations";

const AddVisaType = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(false);

  const [profile_picture_id, setProfile_picture_id] = useState<number | null>(
    null
  );

  const [loading, setLoading] = useState(false);
  const [profileImage, setProfileImage] = useState("");
  const [darkImage, setDarkImage] = useState("");

  const [dark_image_id, set_dark_image_id] = useState<number | null>(null);
  const [getDetailsById] = useLazyVisaTypeDetailsQuery();
  const [updateUser] = useUpdateVisaTypeMutation();

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setProfileImage("");
      return;
    }

    if (
      file &&
      file.type &&
      file.type !== "image/jpeg" &&
      file.type !== "image/jpg" &&
      file.type !== "image/png"
    ) {
      errorToast("Please upload the image in the JPG, JPEG or PNG format");
      return;
    }
    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data?.[0]?.media_url) {
      setProfileImage(imageUrl?.data?.[0]?.media_url || "");
      setProfile_picture_id(imageUrl?.data?.[0]?.id || "");
    }
  };

  const handleDarkImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setDarkImage("");
      return;
    }
    if (
      file &&
      file.type &&
      file.type !== "image/jpeg" &&
      file.type !== "image/jpg" &&
      file.type !== "image/png"
    ) {
      errorToast("Please upload the image in the JPG, JPEG or PNG format");
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data?.[0]?.media_url) {
      setDarkImage(imageUrl?.data?.[0]?.media_url || "");
      set_dark_image_id(imageUrl?.data?.[0]?.id || "");
    }
  };

  const getDetails = async (id: number) => {
    setLoading(true);

    try {
      const response = await getDetailsById({
        id,
      }).unwrap();
      setLoading(false);
      if (response?.status === 200) {
        setProfileImage(response?.data?.visa_type_image?.media_url || "");
        setProfile_picture_id(response?.data?.visa_type_image?.id || 0);
        setDarkImage(response?.data?.visa_type_image_dark?.media_url || "");
        set_dark_image_id(response?.data?.visa_type_image_dark?.id || 0);
        formik.setFieldValue("visaType", response?.data?.visa_type || "-");
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      visaType: "",
    },

    validationSchema: Yup.object({
      visaType: Yup.string()
        .required("Visa type is required")
        .min(2, "Minimum 2 characters are required"),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);
      if (!profileImage || !darkImage) {
        return;
      }

      const body = {
        visa_type_image: profile_picture_id,
        visa_type_image_dark: dark_image_id,
        visa_type: values.visaType,
      };

      try {
        setLoading(true);
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;

        const response = await updateUser({
          body: encryptedBody,
          id: Number(id),
        }).unwrap();

        if (response?.status === 200) {
          setLoading(false);

          successToast("Updated successfully");
          navigate("/visa-type");
        } else {
          setLoading(false);
        }
      } catch (error: any) {
        errorToast(error?.data?.message || "");
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (id) {
      getDetails(Number(id));
    }
  }, [id]);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title fdx">
        <div>
          <h1>{id ? "Edit" : "Add"} Visa Type</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
            <span onClick={() => navigate("/visa-type")}>Visa Types</span> -{" "}
            {id ? "Edit" : "Add"} Visa Type
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_blue" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Light Mode Image</label>
              <div className="upload_box">
                <label>
                  {profileImage && (
                    <IconButton
                      className="crossIcon"
                      onClick={() => setProfileImage("")}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                  <figure>
                    <img
                      src={
                        profileImage
                          ? profileImage
                          : "/static/images/picture.png"
                      }
                      alt="Upload"
                    />
                  </figure>
                  <IconButton className="uploadIcon">
                    <label htmlFor="icon-button-file">
                      <Button component="span" className="upload_image_btn">
                        <img src="/static/images/edit2_icon.svg" alt="Upload" />
                        <Input
                          sx={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{
                            accept: "image/png,image/jpeg",
                          }}
                          onChange={(e: any) => {
                            handleProfileUpload(e);
                            e.target.value = null;
                          }}
                        />
                      </Button>
                    </label>
                  </IconButton>
                </label>
              </div>
              {error && !profileImage ? (
                <h6 className="err_msg">Light mode image is required</h6>
              ) : null}
            </div>
            <div className="control_group w_50">
              <label>Dark Mode Image</label>
              <div className="upload_box">
                <label>
                  {darkImage && (
                    <IconButton
                      className="crossIcon"
                      onClick={() => setDarkImage("")}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                  <figure>
                    <img
                      src={darkImage ? darkImage : "/static/images/picture.png"}
                      alt="Upload"
                    />
                  </figure>
                  <IconButton className="uploadIcon">
                    <label htmlFor="icon-button-file-dark">
                      <Button component="span" className="upload_image_btn">
                        <img src="/static/images/edit2_icon.svg" alt="Upload" />
                        <Input
                          sx={{ display: "none" }}
                          id="icon-button-file-dark"
                          type="file"
                          inputProps={{
                            accept: "image/png,image/jpeg",
                          }}
                          onChange={(e: any) => {
                            handleDarkImageUpload(e);
                            e.target.value = null;
                          }}
                        />
                      </Button>
                    </label>
                  </IconButton>
                </label>
              </div>
              {error && !darkImage ? (
                <h6 className="err_msg">Dark mode image is required</h6>
              ) : null}
            </div>

            <div className="control_group w_50">
              <label>Visa Type</label>
              <TextField
                variant="outlined"
                placeholder="Visa Type"
                fullWidth
                hiddenLabel
                className="text_field"
                name="visaType"
                id="visaType"
                inputProps={{ maxLength: 30 }}
                value={formik.values.visaType}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.visaType && formik.errors.visaType}
              />
            </div>
          </div>

          <div className="form_end_btn">
            <Button
              className="btn btn_secondary"
              type="submit"
              onClick={() => setError(true)}
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddVisaType;
